import { startOfMonth, endOfMonth, getDaysInMonth, format } from 'date-fns';
import { sv } from 'date-fns/locale';

interface BookingInterval {
  check_in: string;
  check_out: string;
}

/**
 * Calculate occupied days within a given month from a list of bookings.
 * Handles cross-month bookings by clamping to month boundaries.
 * Handles overlapping bookings by using a day-set (no double-counting).
 */
export function calculateOccupiedDays(
  bookings: BookingInterval[],
  month: Date = new Date(),
): number {
  const monthStart = startOfMonth(month);
  const monthEnd = endOfMonth(month);
  const startMs = monthStart.getTime();
  const endMs = monthEnd.getTime();

  const occupiedDays = new Set<number>();

  for (const b of bookings) {
    const bStart = new Date(b.check_in).getTime();
    const bEnd = new Date(b.check_out).getTime();

    // Clamp to month
    const clampedStart = Math.max(bStart, startMs);
    const clampedEnd = Math.min(bEnd, endMs);

    if (clampedStart >= clampedEnd) continue;

    // Mark each night (check-in day counts, check-out day doesn't)
    let cursor = clampedStart;
    while (cursor < clampedEnd) {
      const dayOfMonth = new Date(cursor).getDate();
      occupiedDays.add(dayOfMonth);
      cursor += 86_400_000; // +1 day
    }
  }

  return occupiedDays.size;
}

export type OccupancyLevel = 'empty' | 'low' | 'good' | 'high';

export interface OccupancyResult {
  occupiedDays: number;
  totalDays: number;
  rate: number;
  level: OccupancyLevel;
  label: string;
  monthLabel: string;
}

const LEVEL_MAP: { max: number; level: OccupancyLevel; label: string }[] = [
  { max: 0, level: 'empty', label: 'Ej bokat ännu' },
  { max: 0.2, level: 'low', label: 'Nästan tom' },
  { max: 0.5, level: 'low', label: 'Lite bokat' },
  { max: 0.8, level: 'good', label: 'Bra tryck' },
  { max: 1.01, level: 'high', label: 'Nästan fullbokat' },
];

export function getOccupancy(
  bookings: BookingInterval[],
  month: Date = new Date(),
): OccupancyResult {
  const totalDays = getDaysInMonth(month);
  const occupiedDays = calculateOccupiedDays(bookings, month);
  const rate = totalDays > 0 ? occupiedDays / totalDays : 0;

  let level: OccupancyLevel = 'empty';
  let label = LEVEL_MAP[0].label;

  if (occupiedDays === 0) {
    level = 'empty';
    label = LEVEL_MAP[0].label;
  } else {
    for (const entry of LEVEL_MAP.slice(1)) {
      if (rate <= entry.max) {
        level = entry.level;
        label = entry.label;
        break;
      }
    }
  }

  const monthLabel = format(month, 'MMMM', { locale: sv });

  return { occupiedDays, totalDays, rate, level, label, monthLabel };
}

/** Returns filled dot count (0–5) for a visual indicator */
export function getOccupancyDots(rate: number): number {
  if (rate <= 0) return 0;
  if (rate <= 0.2) return 1;
  if (rate <= 0.4) return 2;
  if (rate <= 0.6) return 3;
  if (rate <= 0.8) return 4;
  return 5;
}
